'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Address } from 'shared/types/account';
import { Cart, ShippingMethod } from 'shared/types/cart';
import { CTCreditCardData } from 'shared/types/creditCard/CreditCard';
import { Availability } from 'shared/types/product/InventoryQuery';
import { useCart } from '@/frontastic';
import { ORDER_TYPES } from '../../constants/constant';

export const enum PaymentMethods {
  CARD = 'CARD',
  CREDIT = 'CREDIT',
  CASH = 'CASH',
  CHECK = 'CHECK',
  PAYPAL = 'PAYPAL',
  NO_CHARGE = 'No Charge Order',
}
export const enum CreditScenarios {
  Denied = 'Denied',
  Approved = 'Approved',
  DownPaymentNeeded = 'DownPaymentNeeded',
  Pending = 'Pending',
}

const initialCheckoutState = {
  currentCheckoutStep: 1,
  shippingMethod: null,
  shippingAddress: {} as Address | undefined,
  billingAddress: null,
  selectedPaymentMethod: PaymentMethods.CREDIT,
  completedSteps: [false, false, false], // Track completion status of each step
  tokenizeData: null,
  cardDetails: {} as CTCreditCardData | undefined,
  checkoutExtraDetails: {
    giftMessage: '',
    saveCard: false,
    sendPromotionalEmails: false,
    signatureRequired: false,
  },
  contactInformation: {} as any,
  setShippingMethodValue: (shippingMethod: string) => {},
  setSendPromotionalEmails: (data: boolean) => {},
  setGiftMessage: (data: string) => {},
  setSaveCard: (data: boolean) => {},
  setShippingAddressValue: (shippingAddress: Address | undefined) => {},
  setBillingAddressValue: (shippingAddress: Address) => {},
  goToCheckoutStep: (step: number) => {},
  markStepAsComplete: (step: number) => {},
  markStepAsIncomplete: (step: number) => {},
  selectPaymentMethod: (method: PaymentMethods) => {},
  setTokenizeData: (tokenizeData: any) => {},
  setCardDetails: (creditCard: CTCreditCardData) => {},
  setContactInformation: (contactInformation: any) => {},
  setSelectedPaymentMethods: (method: PaymentMethods) => {},
  orderData: null as any,
  setOrderData: (cardData: any) => {},
  accountAddresses: [] as Address[],
  setAccountAddressesValue: (addresses: Address[]) => {},
  saveAddress: false,
  setSaveAddressFlag: (saveAddress: boolean) => {},
  newCreditCard: false,
  setNewCreditCard: (credit: boolean) => {},
  newAddress: false,
  setNewAddress: (address: boolean) => {},
  creditDetails: {} as any,
  cashDetails: {} as any,
  checkDetails: {} as any,
  setCreditDetails: (credit: any) => {},
  setCashDetails: (cash: any) => {},
  setCheckDetails: (check: any) => {},
  promoCode: '',
  setPromoCodeValue: (promoCode: string) => {},
  resetContextValues: () => {},
  isError: false,
  setError: (isError: boolean) => {},
  notificationMessage: '',
  setNotificationMsg: (message: string) => {},
  setAccountCCValues: (creditCardValues: CTCreditCardData[]) => {},
  savedCreditCards: [] as CTCreditCardData[],
  saveBillingAddress: false,
  setSaveBillingAddressFlag: (flag: boolean) => {},
  showEditAddress: false,
  setShowEditAddressFlag: (flag: boolean) => {},
  preQualData: null as any,
  setPreQualData: (data: any) => {},
  creditDecision: null as any,
  setCreditDecision: (data: any) => {},
  ccIndex: 0 as number,
  setFormValues: (data: any) => {},
  formData: null as any,
  setCCIndexValue: (ccIndex: number) => {},
  encryptedCreditCard: {} as any,
  setEncryptedCCDetails: (encryptedCreditCard: any) => {},
  soaCustomer: {} as any,
  setSoaCustomer: (data: any) => {},
  cartDeliveryDate: '',
  setCartDeliveryDate: (data: any) => {},
  setDeliveryDate: (data: any) => {},
  lastStepPage: '',
  setLastStepPage: (data: any) => {},
  validatePaymentBatchResult: null as any,
  setValidatePaymentBatchResult: (data: any) => {},
  isStepComplete: (step: number): any => {},
  isNoChargeOrder: false,
  setIsNoChargeOrder: (param: any) => {},
  isMailOrder: false,
  setIsMailOrder: (param: any) => {},
  lastEnteredShippingAddress: {},
  setLastEnteredShippingAddress: (param: any) => {},
  setSignatureRequired: (param: any) => {},
  showShippingNotApplicableError: false,
  setShowShippingNotApplicableError: (flag: boolean) => {},
  selectedShippingMethod: {} as ShippingMethod | undefined,
  setSelectedShippingMethod: (data: ShippingMethod) => {},
  shippingZipCode: '',
  setShippingZipCode: (value: string) => {},
  currentAddress: {} as Address,
  setCurrentAddress: (address: Address) => {},
  deliveryStatus: true,
  setDeliveryStatus: (flag: boolean) => {},
  lineItemsAvailibiltyMsgs: [] as Availability[],
  setLineItemsAvailibiltyMsgs: (availability: Availability[]) => {},
  shippingBackorderDate: '',
  setShippingBackorderDate: (date: string) => {},
  customerAccountAddresses: {} as any,
  setCustomerAccountAddresses: (param: any) => {},
  customerDetailsResponse: {} as any,
  setCustomerDetailsResponse: (param: any) => {},
  contextCart: undefined as Cart | undefined,
  setContextCart: (_cart: Cart | undefined) => {},
};

const CheckoutContext = createContext(initialCheckoutState);

export const CheckoutProvider = ({ children }: any) => {
  const [currentCheckoutStep, setCheckoutStep] = useState(1);
  const [orderData, setOrderData] = useState(null) as any;
  const [selectedPaymentMethod, setSelectedPaymentMethods] = useState<any | PaymentMethods>(null);
  const [soaCustomer, setSoaCustomer] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(null as any);
  const [shippingAddress, setShippingAddress] = useState<Address | undefined>({});
  const [billingAddress, setBillingAddress] = useState(null as any);
  const [completedSteps, setCompletedSteps] = useState([false, false, false]); // Initialize completion status
  const [tokenizeData, setTokenizeData] = useState(null);
  const [lastStepPage, setLastStepPage] = useState('');
  const [cardDetails, setCardDetails] = useState({} as any);
  const [creditDetails, setCreditDetails] = useState({} as any);
  const [cashDetails, setCashDetails] = useState({} as any);
  const [checkDetails, setCheckDetails] = useState({} as any);
  const [savedCreditCards, setSavedCreditCards] = useState<CTCreditCardData[]>([]);
  const [saveBillingAddress, setSaveBillingAddress] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [preQualData, setPreQualData] = useState();
  const [creditDecision, setCreditDecision] = useState();
  const [formData, setFormData] = useState(null as any);
  const [validatePaymentBatchResult, setValidatePaymentBatchResult] = useState(null as any);
  const [cartDeliveryDate, setCartDeliveryDate] = useState('');
  const [isNoChargeOrder, setIsNoChargeOrder] = useState(false);
  const [isMailOrder, setIsMailOrder] = useState(false);
  const [lastEnteredShippingAddress, setLastEnteredShippingAddress] = useState({} as any);
  const { data } = useCart(false);
  const [checkoutExtraDetails, setCheckoutExtraDetails] = useState({
    giftMessage: '',
    saveCard: false,
    sendPromotionalEmails: false,
    signatureRequired: false,
    isMailOrder: false,
  });
  const [contactInformation, setContactInformation] = useState({} as any);
  const [accountAddresses, setAccountAddresses] = useState<Address[]>([]);
  const [saveAddress, setSaveAddress] = useState(false);
  const [newAddress, setNewAddress] = useState(false);
  const [newCreditCard, setNewCreditCard] = useState(false);
  const [promoCode, setPromoCode] = useState<string>('');
  const [isError, setIsError] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [ccIndex, setCCIndex] = useState(0);
  const [encryptedCreditCard, setEncryptedCreditCard] = useState({});
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<ShippingMethod>();
  const [showShippingNotApplicableError, setShowShippingNotApplicableError] = useState(false);
  const orderType = data?.additionalDetails?.orderType;
  const [shippingZipCode, setShippingZipCode] = useState('');
  const [currentAddress, setCurrentAddress] = useState<Address>({});
  const [deliveryStatus, setDeliveryStatus] = useState(true);
  const [lineItemsAvailibiltyMsgs, setLineItemsAvailibiltyMsgs] = useState<Availability[]>([]);
  const [shippingBackorderDate, setShippingBackorderDate] = useState('');
  const [customerDetailsResponse, setCustomerDetailsResponse] = useState<any>();
  const [customerAccountAddresses, setCustomerAccountAddresses] = useState<any>();
  const [contextCart, setContextCart] = useState<Cart | undefined>();

  const goToCheckoutStep = (step: number) => {
    setCheckoutStep(step);
  };

  useEffect(() => {
    if (orderType === ORDER_TYPES.NO_CHARGE) {
      setIsNoChargeOrder(true);
      setSelectedPaymentMethods(PaymentMethods.NO_CHARGE);
    } else {
      setIsNoChargeOrder(false);
    }
    if (orderType === ORDER_TYPES.MAIL_ORDER) {
      setIsMailOrder(true);
    } else {
      setIsMailOrder(false);
    }
    setCheckoutStep(1);
  }, [orderType]);

  useEffect(() => {
    setMailOrder(isMailOrder);
  }, [isMailOrder]);

  useEffect(() => {}, [completedSteps]);

  const isStepComplete = (step: number): boolean => {
    if (step - 1 < 0) {
      return false;
    }
    return completedSteps[step - 1];
  };

  const markStepAsComplete = (step: number) => {
    const updatedCompletedSteps = [...completedSteps];
    updatedCompletedSteps[step - 1] = true; // Mark step as completed
    setCompletedSteps(updatedCompletedSteps);
  };
  const setShippingMethodValue = (shippingMethod: string) => {
    setShippingMethod(shippingMethod);
  };
  const setShippingAddressValue = (address: Address | undefined) => {
    setShippingAddress(address);
  };
  const setBillingAddressValue = (shippingAddress: Address) => {
    setBillingAddress(shippingAddress);
  };
  const selectPaymentMethod = (method: PaymentMethods) => {
    setSelectedPaymentMethods(method);
  };
  const setGiftMessage = (data: string) => {
    setCheckoutExtraDetails({
      ...checkoutExtraDetails,
      giftMessage: data,
    });
  };
  const setSaveCard = (data: boolean) => {
    setCheckoutExtraDetails({
      ...checkoutExtraDetails,
      saveCard: data,
    });
  };
  const setSendPromotionalEmails = (data: boolean) => {
    setCheckoutExtraDetails({
      ...checkoutExtraDetails,
      sendPromotionalEmails: data,
    });
  };

  const setSignatureRequired = (signatureRequired: boolean) => {
    setCheckoutExtraDetails({
      ...checkoutExtraDetails,
      signatureRequired: signatureRequired,
    });
  };

  const setMailOrder = (isMailOrder: boolean) => {
    setCheckoutExtraDetails({
      ...checkoutExtraDetails,
      isMailOrder: isMailOrder,
    });
  };

  const markStepAsIncomplete = (step: number) => {
    const updatedCompletedSteps = [...completedSteps];
    updatedCompletedSteps[step - 1] = false; // Mark step as incomplete
    setCompletedSteps(updatedCompletedSteps);
  };

  const setAccountAddressesValue = (addresses: Address[]) => {
    setAccountAddresses(addresses);
  };

  const setSaveAddressFlag = (saveAddress: boolean) => {
    setSaveAddress(saveAddress);
  };

  const setPromoCodeValue = (promoCode: string) => {
    setPromoCode(promoCode);
  };

  const setError = (isError: boolean) => {
    setIsError(isError);
  };

  const setNotificationMsg = (message: string) => {
    setNotificationMessage(message);
  };
  const setFormValues = (data: any) => {
    setFormData({ ...formData, ...data });
  };

  const resetContextValues = () => {
    setCheckoutStep(1);
    setOrderData(null);
    setSelectedPaymentMethods(PaymentMethods.CREDIT);
    setSoaCustomer(null);
    setShippingMethod(null);
    setShippingAddress({});
    setBillingAddress(null);
    setCompletedSteps([false, false, false]);
    setTokenizeData(null);
    setLastStepPage('');
    setCardDetails({});
    setCreditDetails({});
    setCashDetails({});
    setCheckDetails({});
    setSavedCreditCards([]);
    setSaveBillingAddress(false);
    setShowEditAddress(false);
    setPreQualData(undefined);
    setCreditDecision(undefined);
    setFormData(null);
    setValidatePaymentBatchResult(null);
    setCartDeliveryDate('');
    setIsNoChargeOrder(false);
    setIsMailOrder(false);
    setLastEnteredShippingAddress({});
    setCheckoutExtraDetails({
      giftMessage: '',
      saveCard: false,
      sendPromotionalEmails: false,
      signatureRequired: false,
      isMailOrder: false,
    });
    setContactInformation({});
    setAccountAddresses([]);
    setSaveAddress(false);
    setNewAddress(false);
    setNewCreditCard(false);
    setPromoCode('');
    setIsError(false);
    setNotificationMessage('');
    setCCIndex(0);
    setEncryptedCreditCard({});
    setShowShippingNotApplicableError(false);
    setSelectedShippingMethod(undefined);
    setShippingZipCode('');
    setDeliveryStatus(true);
    setLineItemsAvailibiltyMsgs([]);
    setShippingBackorderDate('');
    setCustomerAccountAddresses(undefined);
    setCustomerDetailsResponse(undefined);
    setContextCart(undefined);
  };

  const setAccountCCValues = (creditCards: CTCreditCardData[]) => {
    setSavedCreditCards(creditCards);
  };

  const setSaveBillingAddressFlag = (flag: boolean) => {
    setSaveBillingAddress(flag);
  };
  const setShowEditAddressFlag = (flag: boolean) => {
    setShowEditAddress(flag);
  };
  const setCCIndexValue = (index: number) => {
    setCCIndex(index);
  };

  const setEncryptedCCDetails = (encryptedCC: CTCreditCardData) => {
    setEncryptedCreditCard(encryptedCC);
  };

  const setDeliveryDate = (data: string) => {
    setCartDeliveryDate(data);
  };

  return (
    <CheckoutContext.Provider
      value={{
        currentCheckoutStep,
        shippingMethod,
        completedSteps,
        shippingAddress,
        selectedPaymentMethod,
        setShippingAddressValue,
        goToCheckoutStep,
        markStepAsComplete,
        markStepAsIncomplete,
        setShippingMethodValue,
        selectPaymentMethod,
        tokenizeData,
        setTokenizeData,
        cardDetails,
        setCardDetails,
        setContactInformation,
        contactInformation,
        setBillingAddressValue,
        billingAddress,
        setSelectedPaymentMethods,
        checkoutExtraDetails,
        setGiftMessage,
        setSaveCard,
        setSendPromotionalEmails,
        orderData,
        setOrderData,
        accountAddresses,
        setAccountAddressesValue,
        saveAddress,
        setSaveAddressFlag,
        newAddress,
        setNewAddress,
        creditDetails,
        cashDetails,
        checkDetails,
        setCreditDetails,
        setCashDetails,
        setCheckDetails,
        setPromoCodeValue,
        promoCode,
        notificationMessage,
        setNotificationMsg,
        isError,
        setError,
        resetContextValues,
        savedCreditCards,
        setAccountCCValues,
        saveBillingAddress,
        setSaveBillingAddressFlag,
        preQualData,
        setPreQualData,
        creditDecision,
        setCreditDecision,
        ccIndex,
        setCCIndexValue,
        encryptedCreditCard,
        setEncryptedCCDetails,
        setFormValues,
        formData,
        newCreditCard,
        setNewCreditCard,
        soaCustomer,
        setSoaCustomer,
        cartDeliveryDate,
        setCartDeliveryDate,
        setDeliveryDate,
        showEditAddress,
        setShowEditAddressFlag,
        lastStepPage,
        setLastStepPage,
        isStepComplete,
        validatePaymentBatchResult,
        setValidatePaymentBatchResult,
        isNoChargeOrder,
        setIsNoChargeOrder,
        isMailOrder,
        setIsMailOrder,
        lastEnteredShippingAddress,
        setLastEnteredShippingAddress,
        setSignatureRequired,
        selectedShippingMethod,
        setSelectedShippingMethod,
        showShippingNotApplicableError,
        setShowShippingNotApplicableError,
        shippingZipCode,
        setShippingZipCode,
        currentAddress,
        setCurrentAddress,
        deliveryStatus,
        setDeliveryStatus,
        lineItemsAvailibiltyMsgs,
        setLineItemsAvailibiltyMsgs,
        shippingBackorderDate,
        setShippingBackorderDate,
        customerAccountAddresses,
        setCustomerAccountAddresses,
        customerDetailsResponse,
        setCustomerDetailsResponse,
        contextCart,
        setContextCart,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => useContext(CheckoutContext);
